import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client'; // Use imported client
import { useAuth } from '@/hooks/useAuth';
import { dlog } from '@/utils/devlog';
import { toast } from '@/hooks/use-toast';

const SetPassword = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle invite verification and metadata check
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const type = params.get('type');

    const verifyInvite = async () => {
      if (!user || loading) {
        return;
      }

      setIsVerifying(true);
      setError('');

      const metadata = user.user_metadata || {};

      if (!metadata.requires_password) {
        navigate('/dashboard', { replace: true });
        return;
      }

      // Pre-fill email from user session
      if (user.email) {
        setEmail(user.email);
      } else {
        setError('No email found in session. Please enter your email.');
      }

      // If invite link is present, verify token
      if (token && type === 'invite') {
        try {
          const { data, error } = await supabase.auth.verifyOtp({
            type: 'invite',
            token_hash: token,
            email: user.email || email,
          });

          if (error) {
            console.error('SetPassword: Verify OTP error:', error);
            // Handle token already used (e.g., on refresh)
            if (
              error.message.includes('already used') ||
              error.code === 'token_already_used'
            ) {
              setIsVerifying(false);
              return;
            }
            throw error;
          }

          if (data.session && data.session.user) {
            setEmail(data.session.user.email || email);
          } else {
            throw new Error('No user session created.');
          }
        } catch (err) {
          console.error('SetPassword: Verification error:', err.message, err);
          setError(
            `Failed to verify invite: ${err.message}. Please ensure the email matches the invite and try again.`
          );
        }
      }

      setIsVerifying(false);
    };

    verifyInvite();
  }, [user, loading, navigate, location]);

  // Redirect if authenticated and no password required (non-invite flow)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get('type');

    if (
      user &&
      !loading &&
      type !== 'invite' &&
      !user.user_metadata?.requires_password
    ) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate, location.search]);


  // Handle password update
  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      const { data, error } = await supabase.auth.updateUser({ password });
      if (error) {
        console.error('SetPassword: Password update error:', error);
        throw error;
      }
      if (data.user) {
        // Clear requires_password flag
        const { error: metadataError } = await supabase.auth.updateUser({
          data: { requires_password: false },
        });
        if (metadataError) {
          toast({
            variant: 'destructive',
            title: 'Partial Success',
            description:
              'Password set, but failed to update user metadata. You can still log in.',
          });
          setError(
            'Password set, but failed to update user metadata. You can still log in.'
          );
          return; // Don't set success if metadata failed
        }
        await supabase.auth.refreshSession();
        await supabase.auth.getUser();
        toast({
          title: 'Success',
          description: 'Password set successfully! Redirecting to dashboard...',
        });
        setSuccess(true);
      }
    } catch (err) {
      console.error('SetPassword: Password update error:', err.message);
      setError(`Error setting password: ${err.message}`);
    }
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        // Use full reload to ensure fresh session
        window.location.assign('/dashboard');
      }, 3000); // Increased to 3s for user to see message
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (success) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4'>
        <div className='w-full max-w-md space-y-8'>
          <div className='text-center space-y-4'>
            <img
              src='/images/8e8b8d0c-2b0f-47d3-aed3-98e19c548c8d.png'
              alt='CallSparker'
              className='h-16 mx-auto'
            />
            <p className='text-muted-foreground'>
              Power your B2B voice prospecting with AI
            </p>
          </div>
          <Card className='border-2'>
            <CardHeader className='space-y-1'>
              <CardTitle className='text-2xl font-semibold'>Success</CardTitle>
              <CardDescription>
                Your password has been set. Redirecting to dashboard...
              </CardDescription>
            </CardHeader>
            <CardContent className='text-center'>
              <Button
                onClick={() => window.location.assign('/dashboard')}
                className='w-full h-11'
              >
                Go to Dashboard Now
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4'>
      <div className='w-full max-w-md space-y-8'>
        {/* Logo and Header */}
        <div className='text-center space-y-4'>
          <div className='flex items-center justify-center'>
            <img
              src='/images/8e8b8d0c-2b0f-47d3-aed3-98e19c548c8d.png'
              alt='CallSparker'
              className='h-16'
            />
          </div>
          <p className='text-muted-foreground'>
            Power your B2B voice prospecting with AI
          </p>
        </div>

        {/* Auth Form */}
        <Card className='border-2'>
          <CardHeader className='space-y-1'>
            <CardTitle className='text-2xl font-semibold'>Sign Up</CardTitle>
            <CardDescription>
              Create a password for your new CallSparker account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && <div className='mb-4 text-red-500 text-sm'>{error}</div>}
            {isVerifying && !error && (
              <div className='mb-4 text-sm text-muted-foreground'>
                Verifying invite link...
              </div>
            )}
            <form onSubmit={handleSetPassword} className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='email'>Email</Label>
                <Input
                  id='email'
                  type='email'
                  placeholder='your@email.com'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className='h-11'
                  disabled={!!user?.email} // Disable if pre-filled from session
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='password'>New Password</Label>
                <div className='relative'>
                  <Input
                    id='password'
                    type={showPassword ? 'text' : 'password'}
                    placeholder='Create your password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className='h-11 pr-10'
                    disabled={isVerifying}
                  />
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent'
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className='h-4 w-4 text-muted-foreground' />
                    ) : (
                      <Eye className='h-4 w-4 text-muted-foreground' />
                    )}
                  </Button>
                </div>
              </div>

              <Button
                type='submit'
                className='w-full h-11'
                disabled={loading || isVerifying}
              >
                {loading ? 'Processing...' : 'Create Account'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Private Beta Notice */}
        <Card className='bg-muted/50 border-dashed'>
          <CardContent className='pt-6'>
            <div className='text-center space-y-3'>
              <p className='text-sm font-medium text-muted-foreground'>
                Private Beta
              </p>
              <p className='text-xs text-muted-foreground'>
                CallSparker is currently in private beta. Contact us for access
                information.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SetPassword;
